import { soDigitos } from "./mascaras";

// Busca de endereço por CEP, no ViaCEP.
//
// ---------------------------------------------------------------------------
// Uma dependência externa no caminho de um estranho — e as regras disso
// ---------------------------------------------------------------------------
// Quem usa isto é o CLIENTE do aluno, na página pública do formulário. É a única
// chamada do hub a um domínio que não é nosso feita do navegador de alguém sem
// conta, e por isso ela obedece a três regras:
//
//   1. **Falha é INOFENSIVA.** ViaCEP fora do ar, sem internet, CEP que não existe
//      — tudo devolve `null` e o formulário continua igual, com os campos de
//      endereço abertos para digitar. Nada bloqueia, nada avisa em vermelho: o
//      endereço todo é opcional (só serve ao contrato, se o cliente fechar).
//   2. **Nada nosso vai para lá.** A requisição leva só os oito dígitos do CEP.
//      Não vai nome, não vai CPF, não vai o token do formulário — o ViaCEP não
//      fica sabendo que existe uma proposta, nem de quem.
//   3. **Timeout curto.** Cinco segundos com `AbortController`. Sem ele, um
//      serviço lento deixaria o campo "buscando…" para sempre, e a pessoa não sabe
//      se pode digitar.
//
// Escolhido o ViaCEP e não a API dos Correios porque não pede chave, aceita CORS de
// qualquer origem e é o que o mercado brasileiro usa há mais de uma década. Se um
// dia sair do ar, o formulário não quebra — só volta a ser digitado à mão, que é o
// que ele era antes disto.
//
// ---------------------------------------------------------------------------
// O que a resposta NÃO decide
// ---------------------------------------------------------------------------
// O endereço preenchido é sugestão: os campos continuam editáveis. Prédio novo,
// loteamento e correção de logradouro acontecem, e o ViaCEP não é a autoridade
// sobre onde a pessoa mora — ela é. Sobrescrever o que ela já digitou seria trocar
// o conhecimento dela pelo de um terceiro.

const VIACEP = "https://viacep.com.br/ws";
const TEMPO_LIMITE = 5000;

export type EnderecoDoCep = {
  endereco: string;
  bairro: string;
  cidade: string;
  estado: string;
};

/**
 * Nunca lança, e nunca demora mais que cinco segundos.
 *
 * Devolve `null` para CEP incompleto, CEP inexistente, rede fora e resposta em
 * formato inesperado — os quatro dão a mesma coisa de propósito, porque a ação do
 * cliente é a mesma nos quatro: digitar o endereço.
 */
export async function buscarCep(cep: string): Promise<EnderecoDoCep | null> {
  const d = soDigitos(cep);
  if (d.length !== 8) return null;

  const cancelar = new AbortController();
  const relogio = setTimeout(() => cancelar.abort(), TEMPO_LIMITE);

  try {
    const r = await fetch(`${VIACEP}/${d}/json/`, { signal: cancelar.signal });
    if (!r.ok) return null;

    const c = await r.json();
    // O ViaCEP responde 200 com `{ "erro": true }` para CEP que não existe — não é
    // 404. Sem esta linha, um CEP inventado limparia os campos do endereço.
    if (!c || c.erro) return null;

    const endereco = String(c.logradouro ?? "").trim();
    const cidade = String(c.localidade ?? "").trim();
    // Sem logradouro nem cidade não há o que preencher. Acontece com CEP geral de
    // município, e devolver um objeto vazio faria a tela piscar sem mudar nada.
    if (!endereco && !cidade) return null;

    return {
      endereco,
      bairro: String(c.bairro ?? "").trim(),
      cidade,
      estado: String(c.uf ?? "").trim().toUpperCase(),
    };
  } catch {
    // AbortError, falha de rede, JSON inválido — a resposta é a mesma.
    return null;
  } finally {
    clearTimeout(relogio);
  }
}
